import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft, Trophy, Users, ShieldQuestion,
  ChevronRight, Search,
} from 'lucide-react';
import { fetchPublicTeamRoster, friendlyError, buildAssetUrl } from '@/lib/api';
import { resolvePlayerAvatar } from '@/lib/playerAvatar';

export default function TeamRosterPublic() {
  const { clubSlug, teamSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPublicTeamRoster(clubSlug, teamSlug)
      .then((d) => { if (alive) setData(d); })
      .catch((err) => { if (alive) setError(friendlyError(err)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [clubSlug, teamSlug]);

  const filteredItems = useMemo(() => {
    const items = data?.items || [];
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => (p.full_name || '').toLowerCase().includes(q));
  }, [data?.items, query]);

  if (loading) {
    return (
      <MainLayout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-10 w-64 mb-6" />
          <Skeleton className="h-96 rounded-2xl" />
        </div>
      </MainLayout>
    );
  }

  if (error || !data) {
    return (
      <MainLayout>
        <div data-testid="team-roster-error" className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <ShieldQuestion className="h-12 w-12 text-[var(--ns-text-muted)] mx-auto mb-4" />
          <h1 className="font-display text-2xl text-[var(--ns-text)] mb-2">Plantilla no disponible</h1>
          <p className="text-sm text-[var(--ns-text-muted)] mb-6">
            Este equipo no tiene plantilla pública o no se ha autorizado su visualización.
          </p>
          <Button asChild variant="outline" className="rounded-xl">
            <Link to={`/clubes/${clubSlug}`}>← Volver al club</Link>
          </Button>
        </div>
      </MainLayout>
    );
  }

  const { club, team, total } = data;
  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <Link
            to={`/clubes/${clubSlug}`}
            className="inline-flex items-center gap-1 text-sm text-[var(--ns-text-muted)] hover:text-[var(--ns-gold-700)]"
          >
            <ArrowLeft className="h-4 w-4" /> Volver a {club?.name || 'club'}
          </Link>
        </div>

        <header className="flex items-start gap-4 mb-8">
          {club?.logo_url ? (
            <img src={buildAssetUrl(club.logo_url)} alt={club.name} className="h-16 w-16 rounded-xl border border-[var(--ns-border)] object-contain bg-white p-1.5" />
          ) : (
            <div className="h-16 w-16 rounded-xl border border-[var(--ns-border)] bg-[var(--ns-bg-2)] flex items-center justify-center">
              <Trophy className="h-7 w-7 text-[var(--ns-text-subtle)]" />
            </div>
          )}
          <div className="flex-1">
            <p className="text-xs uppercase tracking-widest text-[var(--ns-text-muted)] font-semibold">{club?.name}</p>
            <h1 data-testid="team-roster-title" className="font-display text-2xl sm:text-3xl text-[var(--ns-text)]">
              {team?.name}
            </h1>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {team?.category ? <Badge className="bg-[var(--ns-amber-50)] text-[var(--ns-gold-700)] uppercase text-[10px]">{team.category}</Badge> : null}
              {team?.gender ? <Badge className="bg-[var(--ns-bg-2)] text-[var(--ns-text)] uppercase text-[10px]">{team.gender}</Badge> : null}
              {team?.sport_name ? <Badge variant="outline" className="text-[10px] uppercase">{team.sport_name}</Badge> : null}
              {team?.season ? <span className="text-xs text-[var(--ns-text-muted)]">Temporada {team.season}</span> : null}
            </div>
          </div>
          <div className="text-right">
            <div className="inline-flex items-center gap-1 text-sm text-[var(--ns-text-muted)]">
              <Users className="h-4 w-4" /> {total} {total === 1 ? 'jugador' : 'jugadores'}
            </div>
          </div>
        </header>

        {total === 0 ? (
          <Card data-testid="team-roster-empty" className="p-10 rounded-2xl bg-white border border-dashed border-[var(--ns-border-strong)] text-center">
            <Users className="h-10 w-10 text-[var(--ns-text-subtle)] mx-auto mb-3" />
            <p className="text-sm text-[var(--ns-text-muted)]">
              Todavía no hay jugadores visibles públicamente en esta plantilla.
            </p>
          </Card>
        ) : (
          <Card
            data-testid="team-roster-list"
            className="rounded-2xl bg-white border border-[var(--ns-border)] overflow-hidden"
          >
            {/* Cabecera de la lista + buscador */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 p-4 md:p-6 border-b border-[var(--ns-border)]">
              <div>
                <h2 className="font-display text-xl text-[var(--ns-text)]">Plantilla de jugadores</h2>
                <p className="text-xs text-[var(--ns-text-muted)] mt-1">
                  Listado de jugadores de {club?.name}
                  {team?.name ? <> · {team.name}</> : null}
                  {team?.season ? <> · Temporada {team.season}</> : null}
                </p>
              </div>
              <div className="relative w-full md:w-72">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-[var(--ns-text-muted)] pointer-events-none" />
                <Input
                  data-testid="team-roster-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar jugador…"
                  className="pl-9 h-9 rounded-lg bg-[var(--ns-bg-2)]"
                />
              </div>
            </div>

            {/* Tabla desktop */}
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead className="bg-white">
                  <tr className="text-left text-[11px] uppercase tracking-wide text-[var(--ns-text-muted)] border-b border-[var(--ns-border)]">
                    <th className="px-6 py-3 font-medium w-20">Foto</th>
                    <th className="px-4 py-3 font-medium">Jugador</th>
                    <th className="px-4 py-3 font-medium w-24">Edad</th>
                    <th className="px-4 py-3 font-medium w-56">Posición</th>
                    <th className="px-4 py-3 font-medium w-40">Evaluación</th>
                    <th className="px-6 py-3 font-medium w-32 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-sm text-[var(--ns-text-muted)]">
                        Ningún jugador coincide con la búsqueda.
                      </td>
                    </tr>
                  ) : filteredItems.map((p) => (
                    <PlayerRow key={p.slug} player={p} clubSlug={clubSlug} teamSlug={teamSlug} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Lista compacta móvil */}
            <div className="md:hidden divide-y divide-[var(--ns-border)]">
              {filteredItems.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-[var(--ns-text-muted)]">
                  Ningún jugador coincide con la búsqueda.
                </div>
              ) : filteredItems.map((p) => (
                <PlayerRowMobile key={p.slug} player={p} clubSlug={clubSlug} teamSlug={teamSlug} />
              ))}
            </div>
          </Card>
        )}
      </div>
    </MainLayout>
  );
}

function _positionLabel(p) {
  return p.position || '—';
}

function _evaluationLabel() {
  // Dato inexistente todavía: mostramos placeholder visual único, sin
  // calcular ni inventar puntuaciones. Cuando exista se sustituye aquí.
  return 'Próximamente';
}

function PlayerAvatar({ player }) {
  const avatar = resolvePlayerAvatar({ profile_photo: player.profile_photo, gender: player.gender });
  const src = avatar && avatar.startsWith('http') ? avatar : buildAssetUrl(avatar);
  if (!src) {
    return (
      <div className="h-10 w-10 rounded-full bg-[var(--ns-bg-2)] border border-[var(--ns-border)] flex items-center justify-center">
        <Trophy className="h-4 w-4 text-[var(--ns-text-subtle)]" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={player.full_name}
      className="h-10 w-10 rounded-full object-cover border border-[var(--ns-border)]"
      loading="lazy"
    />
  );
}

function ViewProfileButton({ player, clubSlug, teamSlug }) {
  if (!player.has_full_profile) {
    return (
      <span className="text-xs text-[var(--ns-text-subtle)]">Perfil no disponible</span>
    );
  }
  return (
    <Button
      asChild
      size="sm"
      data-testid={`team-roster-view-${player.slug}`}
      className="h-8 rounded-lg bg-gradient-to-r from-[var(--ns-gold)] to-[var(--ns-gold-600)] text-[#1c1612] text-xs font-semibold hover:from-[var(--ns-gold-600)] hover:to-[var(--ns-gold-700)] hover:text-white border-0"
    >
      <Link
        to={`/clubes/${clubSlug}/equipos/${teamSlug}/jugadores/${player.slug}`}
        className="inline-flex items-center gap-1"
      >
        Ver perfil <ChevronRight className="h-3.5 w-3.5" />
      </Link>
    </Button>
  );
}

function PlayerRow({ player, clubSlug, teamSlug }) {
  return (
    <tr
      data-testid={`team-roster-row-${player.slug}`}
      className="border-b border-[var(--ns-border)] last:border-b-0 hover:bg-[var(--ns-bg-2)]/40 transition-colors"
    >
      <td className="px-6 py-3 align-middle">
        <PlayerAvatar player={player} />
      </td>
      <td className="px-4 py-3 align-middle">
        <div className="font-semibold text-[var(--ns-text)] truncate">{player.full_name}</div>
      </td>
      <td className="px-4 py-3 align-middle text-[var(--ns-text)] tabular-nums">
        {player.age != null ? player.age : '—'}
      </td>
      <td className="px-4 py-3 align-middle text-[var(--ns-text-muted)] truncate">
        {_positionLabel(player)}
      </td>
      <td className="px-4 py-3 align-middle text-[var(--ns-text-subtle)]">
        {_evaluationLabel()}
      </td>
      <td className="px-6 py-3 align-middle text-right">
        <ViewProfileButton player={player} clubSlug={clubSlug} teamSlug={teamSlug} />
      </td>
    </tr>
  );
}

function PlayerRowMobile({ player, clubSlug, teamSlug }) {
  return (
    <div
      data-testid={`team-roster-row-${player.slug}`}
      className="flex items-center gap-3 p-4"
    >
      <PlayerAvatar player={player} />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[var(--ns-text)] truncate">{player.full_name}</div>
        <div className="text-xs text-[var(--ns-text-muted)] truncate">
          {player.age != null ? `${player.age} años` : '—'} · {_positionLabel(player)}
        </div>
        <div className="text-[11px] text-[var(--ns-text-subtle)] mt-0.5">
          Evaluación: {_evaluationLabel()}
        </div>
      </div>
      <ViewProfileButton player={player} clubSlug={clubSlug} teamSlug={teamSlug} />
    </div>
  );
}
